/**
 * Base Event Builder
 * Provides common functionality for building all tracking events
 */

import snakecaseKeys from 'snakecase-keys';
import { BaseEvent, GaEventType } from '../types/index.js';

/**
 * Supplies the A/B test variant for events that don't set one explicitly.
 */
export type AbTestVariantProvider = () => string | null;

interface AbTestVariantRegistry {
    /**
     * The package never resolves a variant on its own — until an app registers
     * a provider, events without an explicit abTestVariant carry no variant.
     */
    provider: AbTestVariantProvider;
    /**
     * Whether the registered provider has already thrown, so the warning is logged
     * once instead of on every event (a page can fire dozens of them).
     */
    providerFailed: boolean;
}

type AbTestVariantRegistryHost = typeof globalThis & {
    __skydreamsTrackingAbTestVariant?: AbTestVariantRegistry;
};

/**
 * The registry lives on globalThis rather than in module scope because a page
 * routinely runs more than one copy of this package — app_b2b ships an esbuild
 * component bundle and an AssetMapper/importmap browser bundle side by side, and
 * each copy would otherwise keep its own provider. Whichever copy happens to win
 * `window.DataLayerService` must see the provider the app registered, wherever
 * it was registered from.
 */
function getAbTestVariantRegistry(): AbTestVariantRegistry {
    const host = globalThis as AbTestVariantRegistryHost;

    if (!host.__skydreamsTrackingAbTestVariant) {
        host.__skydreamsTrackingAbTestVariant = {
            provider: () => null,
            providerFailed: false,
        };
    }

    return host.__skydreamsTrackingAbTestVariant;
}

/**
 * Registers the app's source for the auto-resolved A/B test variant. The
 * package deliberately has no default (it must not depend on any app's DOM
 * or experiment conventions) — each app registers its own provider once at
 * bootstrap, e.g. () => document.body.getAttribute('data-ab') or a call
 * into an experiment service. A caller-supplied abTestVariant still wins.
 */
export function setAbTestVariantProvider(provider: AbTestVariantProvider): void {
    const registry = getAbTestVariantRegistry();

    registry.provider = provider;
    registry.providerFailed = false;
}

/**
 * Calls the app-registered provider defensively. It is third-party code — an
 * experiment SDK or a DOM read that may run before document.body exists — and a
 * throwing provider must degrade to "no variant" rather than take down every
 * event on the page. The variant is deliberately omitted rather than guessed: a
 * fabricated value would silently corrupt experiment reporting.
 *
 * The provider keeps being called on later events, so it self-heals once the
 * experiment framework has booted.
 */
function resolveAbTestVariant(): string | null {
    const registry = getAbTestVariantRegistry();

    try {
        return registry.provider();
    } catch (error) {
        if (!registry.providerFailed) {
            registry.providerFailed = true;
            console.warn(
                'DataLayerService: abTestVariantProvider threw; events are sent without ab_test_variant',
                error
            );
        }

        return null;
    }
}

/**
 * Builds a base event with common properties that all events should have
 * - Converts all options from camelCase to snake_case
 * - Resolves the A/B test variant (see applyAbTestVariant)
 * - Ensures consistent event structure across all event types
 *
 * @param eventType The type of event to build
 * @param options The event options in camelCase
 * @returns The built event with snake_case properties and common fields
 */
export function buildBaseEvent<T extends BaseEvent>(
    eventType: GaEventType,
    options: Record<string, any>
): T {
    // Convert all options to snake_case
    const snakeOptions = snakecaseKeys(options as any, { deep: true });

    const event = {
        event: eventType,
        ...snakeOptions,
    } as T;

    applyAbTestVariant(event, options);

    return event;
}

/**
 * Resolves the ab_test_variant on the event:
 * - caller-supplied value (camelCase `abTestVariant` or, from untyped JS
 *   callers, snake_case `ab_test_variant`) always wins over the
 *   registered provider
 * - explicit `null` suppresses the provider and clears the key (for events
 *   that must never carry a variant)
 * - absent or `undefined` resolves via the app-registered provider;
 *   without one the key is omitted
 *
 * Note the difference between clearing and omitting. GTM's data model persists
 * keys across pushes, so an omitted key leaves whatever an earlier event set
 * still applying. Explicit `null` is an assertion — "this event carries no
 * variant" — and has to push a value to override the earlier one, so the key is
 * kept with `undefined`, the same way every other field clears (see the
 * undefined-value test in base-event-builder.spec.ts). Absent/`undefined` with
 * no provider is not an assertion but an absence of opinion, so it stays omitted.
 */
function applyAbTestVariant(event: BaseEvent, options: Record<string, unknown>): void {
    const callerVariant = options.abTestVariant !== undefined
        ? options.abTestVariant
        : options.ab_test_variant;

    // snakecaseKeys has already copied any caller spelling onto the event; drop it
    // so exactly one of the branches below decides what the key ends up as.
    delete event.ab_test_variant;

    if (callerVariant !== undefined) {
        event.ab_test_variant = callerVariant === null
            ? undefined
            : callerVariant as string;

        return;
    }

    const abTestVariant = resolveAbTestVariant();
    if (abTestVariant) {
        event.ab_test_variant = abTestVariant;
    }
}
